import { fetchMenuItems } from '@nx-frontend/shared/api';
import {
  MenuItem,
  MenuItemType,
  MenuType,
  PortalServiceMenuItemDto,
  Tag,
  TranslatesFnType,
} from '@nx-frontend/shared/types';
import { MenusState, MenuState } from '@nx-frontend/shared/types';
import { generateTagId, isValidUrl } from '@nx-frontend/shared/utils';

export function addIdToOrder(order: string[], id: string, index: number) {
  if (index === 0) {
    return [id, ...order.filter((childId) => childId !== id)];
  } else if (order.length < index) {
    return [...order.filter((childId) => childId !== id), id];
  } else {
    const newOrder = [...order];
    const currentIndex = order.indexOf(id);
    if (currentIndex !== -1) {
      newOrder.splice(currentIndex, 1);
    }
    newOrder.splice(index, 0, id);
    return newOrder;
  }
}

export function getMenuSlugs(state: MenusState, menuTypes: MenuType[]) {
  return menuTypes.reduce((acc, menuType) => {
    const menuItems = Object.values(state[menuType].entities);
    menuItems.forEach((menuItem) => {
      if (typeof menuItem.url === 'string') {
        acc.push(menuItem.url.split('/').pop() || '');
      }
      if (typeof menuItem.originalUrl === 'string') {
        acc.push(menuItem.originalUrl.split('/').pop() || '');
      }
    });
    return acc;
  }, [] as string[]);
}

export function getMenuItemsByIds(state: MenusState, menuType: MenuType, ids: string[]): MenuItem[] {
  const entities = state[menuType].entities;

  return ids.reduce((acc, id) => {
    const item = entities[id];
    if (item) {
      acc.push(item);
    }
    return acc;
  }, [] as MenuItem[]);
}

export function getActiveIds(itemsMap: Record<string, MenuItem>, activeIds: string[], toggleId: string): string[] {
  if (activeIds.includes(toggleId)) {
    return activeIds.filter((activeId) => activeId !== toggleId);
  }

  const item = itemsMap[toggleId];
  const parentItem = itemsMap[item.parentId];
  const parentIds = getItemParentIds(itemsMap, toggleId);
  const isParentActive = activeIds.some((activeId) => parentIds.includes(activeId));

  if (item && isParentActive) {
    if (parentItem && parentItem.order) {
      return parentIds.reduce((acc, parentId) => {
        const parentItem = itemsMap[parentId];
        const parentOrderIds =
          parentItem && parentItem.order
            ? parentItem.order.filter((orderId) => !parentIds.includes(orderId) && orderId !== toggleId)
            : [];
        return [...acc.filter((accId) => accId !== parentId), ...parentOrderIds];
      }, activeIds);
    }
  }

  if (item.hasChildren) {
    const childrenIds = getItemChildrenIds(itemsMap, toggleId);
    return [...activeIds.filter((activeId) => !childrenIds.includes(activeId)), toggleId];
  }

  return [...activeIds, toggleId];
}

export function getItemChildrenIds(itemsMap: Record<string, MenuItem>, parentKey: string): string[] {
  const parentItem = itemsMap[parentKey];
  if (!parentItem || !parentItem.hasChildren || !parentItem.order) {
    return [];
  }

  return parentItem.order.reduce((acc, childId) => {
    acc.push(childId);
    acc.push(...getItemChildrenIds(itemsMap, childId));
    return acc;
  }, [] as string[]);
}

export function getItemParentIds(itemsMap: Record<string, MenuItem>, itemId: string): string[] {
  const item = itemsMap[itemId];
  if (!item) {
    return [];
  }

  if (item.parentId.length) {
    return [...getItemParentIds(itemsMap, item.parentId), item.parentId];
  }
  return [];
}

export const menuItemTypeLocMap: Partial<Record<MenuItemType, string>> = {
  [MenuItemType.ALL_PAGES]: 'sidebar.sidebar-all_pages',
  [MenuItemType.FILES_MANAGER]: 'sidebar.sidebar-files',
  [MenuItemType.TASKS]: 'sidebar.sidebar-tasks',
  [MenuItemType.TAGS]: 'sidebar.sidebar-tags',
  [MenuItemType.AI_AGENTS]: 'sidebar.sidebar-ai-agents',
  [MenuItemType.CHAT]: 'sidebar.sidebar-chat',
  [MenuItemType.FOLDERS]: 'sidebar.sidebar-folders',
  [MenuItemType.HOME]: 'sidebar.sidebar-home',
  [MenuItemType.FILES]: 'sidebar.sidebar-files',
};

function createMenuItem(item: PortalServiceMenuItemDto, existedItem?: MenuItem, t?: TranslatesFnType): MenuItem {
  let name = item.type === MenuItemType.HOME && !item.name?.length ? 'Homepage' : item.name;
  if (t) {
    const locKey = menuItemTypeLocMap[item.type];
    name = (locKey && t(locKey)) ?? name;
  }

  const icon = item.type === MenuItemType.HOME ? 'home' : item.icon ?? '';
  const order = item.children?.map((child) => (item as { key?: string }).key ?? child.id);
  const iconIsUrl = isValidUrl(item.icon as string);
  const targetId = 'targetId' in item ? item.targetId : '';
  const positionType = 'positionType' in item ? item.positionType : '';
  const updatedAt = 'updatedAt' in item ? item.updatedAt : '';
  const createdAt = 'createdAt' in item ? item.createdAt : '';
  const isShared = 'isShared' in item ? item.isShared : '';
  const permissions = 'permissions' in item ? item.permissions : '';

  const preparedItem = {
    ...existedItem,
    type: item.type,
    id: item.id,
    parentId: item.parentId,
    hasChildren: item.hasChildren,
    icon,
    name,
    index: item.index,
    url: item.url ?? '',
    slug: item.slug ?? '',
    isOrigin: item.isOrigin,
    originGlobalId: item.originGlobalId,
    ...(order ? { order } : null),
    ...(permissions ? { permissions } : null),
    ...(targetId ? { targetId } : null),
    ...(positionType ? { positionType } : null),
    ...(updatedAt ? { updatedAt } : null),
    ...(createdAt ? { createdAt } : null),
    ...(isShared ? { isShared } : null),
    ...('pageId' in item ? { pageId: item.pageId } : null),
    ...('visible' in item ? { visible: item.visible } : null),
    ...('openInNewTab' in item ? { openInNewTab: item.openInNewTab } : null),
    ...('process' in item ? { process: item.process } : null),
  } as MenuItem;

  if (iconIsUrl) {
    preparedItem.iconUrl = item.icon;
  }

  return preparedItem;
}

export function tagToMenuItemDto(tag: Tag): PortalServiceMenuItemDto {
  return {
    name: tag.name,
    id: `${MenuItemType.TAG}#${generateTagId(tag.name)}`,
    positionType: 'sidebar',
    type: MenuItemType.TAG,
    parentId: `${MenuItemType.TAGS}#root`,
    index: 0,
    visible: true,
    url: '',
    slug: '',
    updatedAt: 0,
    openInNewTab: false,
    hasChildren: false,
  };
}

export function createTagMenuItem(tag: Tag): MenuItem {
  return mapDTOtoMenuItem(tagToMenuItemDto(tag));
}

// Функция для подготовки всех элементов меню
export function prepareMenuItemsData(
  items: PortalServiceMenuItemDto[],
  t?: TranslatesFnType
): {
  rootOrder: string[];
  items: Record<string, MenuItem>;
} {
  const rootOrder: string[] = [];
  const preparedItems = getFlatArray(items).reduce((acc, item) => {
    const id = (item as { key?: string }).key ?? item.id;
    const parentId = (item as { parentKey?: string }).parentKey ?? item.parentId;
    const existedItem = acc[id];

    if (parentId === '') {
      rootOrder.push(id);
    }

    acc[id] = createMenuItem(item, existedItem, t);
    return acc;
  }, {} as Record<string, MenuItem>);

  return {
    rootOrder,
    items: preparedItems,
  };
}

// Функция для преобразования одного элемента меню
export function mapDTOtoMenuItem(item: PortalServiceMenuItemDto): MenuItem {
  return createMenuItem(item);
}

export function getFlatArray(items: PortalServiceMenuItemDto[]): PortalServiceMenuItemDto[] {
  return items
    .sort((a, b) => a.index - b.index)
    .reduce<PortalServiceMenuItemDto[]>((acc, item) => {
      acc.push(item);
      if (item.children) {
        acc.push(...getFlatArray(item.children));
      }
      return acc;
    }, []);
}

export function getMenuTypeFromPositionType(positionType: string): MenuType | undefined {
  if (positionType === 'sidebar') {
    return MenuType.PORTAL_SIDEBAR;
  } else if (positionType === 'top') {
    return MenuType.PORTAL_TOPBAR;
  } else if (positionType === 'footer') {
    return MenuType.PORTAL_FOOTER;
  }
  return undefined;
}

export function menuItemsToMenuState(
  items: PortalServiceMenuItemDto[],
  path?: string,
  t?: TranslatesFnType
): MenuState {
  const { items: entities, rootOrder } = prepareMenuItemsData(items, t);
  const activeItemIds = path ? getMenuActiveItemKeys(items, path) : [];
  return {
    entities,
    rootOrder,
    activeItemIds,
    ids: Object.keys(entities),
  };
}

export function getMenuActiveItemKeys(items: PortalServiceMenuItemDto[], path: string): string[] {
  return items.reduce((acc, item) => {
    if (item.url?.toLowerCase() === path?.toLowerCase()) {
      acc.push(item.id);
    }
    if (item.children) {
      acc.push(...getMenuActiveItemKeys(item.children, path));
    }
    return acc;
  }, [] as string[]);
}

export function updateItemSlug(
  state: MenusState,
  menuType: MenuType,
  { id, slug }: Pick<MenuItem, 'id' | 'slug'>,
  oldSlug?: string
): MenuItem[] {
  const menu = state[menuType];
  const entities = menu.entities;

  const updatedItems: MenuItem[] = [];
  const updateItem = { ...entities[id] };
  const itemSlug = updateItem?.slug;

  if (updateItem) {
    const newUrl = updateItem.url.replace(`/${oldSlug ?? updateItem.slug}`, `/${slug}`);

    if (!updateItem.originalUrl) {
      updateItem.originalUrl = updateItem.url;
    }

    if (!oldSlug) {
      updateItem.slug = slug;
    }

    updateItem.url = newUrl;
    if (newUrl === updateItem.originalUrl) {
      delete updateItem['originalUrl'];
    }
  }

  updatedItems.push(updateItem);
  if (updateItem.order) {
    updateItem.order.forEach((childId) => {
      const items = updateItemSlug(state, menuType, { id: childId, slug }, !oldSlug ? itemSlug : oldSlug);
      updatedItems.push(...items);
    });
  }
  return updatedItems;
}

export const getRootMenuDropAreaId = (menuType: MenuType): string => `${menuType}-root`;

export function isFolderMenuItem(menuItemType: MenuItemType): boolean {
  return (
    menuItemType === MenuItemType.FOLDERS ||
    menuItemType === MenuItemType.NOTES_FOLDER ||
    menuItemType === MenuItemType.PORTAL_SECTION ||
    menuItemType === MenuItemType.PORTAL_PROCESS
  );
}

export function getItemOffset(level: number, permissionPreviewEnabled = false) {
  return level * 16 + (permissionPreviewEnabled && level > 0 ? 38 : 0) + 'px';
}

export class MenuItemAbortedError extends Error {}

export async function waitForMenuItem(
  newId: string,
  portalId: string,
  intervalMs: number,
  timeoutMs: number,
  originalAllowUserIds: number[]
): Promise<PortalServiceMenuItemDto[]> {
  const abortController = new AbortController();
  const timeout = setTimeout(() => abortController.abort(), timeoutMs);
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (abortController.signal.aborted) {
      throw new MenuItemAbortedError();
    }

    const menus = await fetchMenuItems({ portalId });

    // const sidebarMenus = menus.filter((item) => item.positionType === 'sidebar');
    // const foundItem = getFlatArray(sidebarMenus).find((item) => item.positionType === 'sidebar' && item.id === newId);

    const foundItem = getFlatArray(menus).find((item) => item.id === newId);

    if (
      foundItem &&
      foundItem.permissions &&
      comparePermissions(foundItem.permissions.allowUserIds, originalAllowUserIds)
    ) {
      clearTimeout(timeout);
      return menus.filter((item) => item.positionType === foundItem.positionType);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

function comparePermissions(newAllowUserIds: number[], originalAllowUserIds: number[]): boolean {
  if (newAllowUserIds.length !== originalAllowUserIds.length) {
    return false;
  }
  return newAllowUserIds.every((id) => originalAllowUserIds.includes(id));
}
